import { useAiChatChat } from "@crowdcoursing/api/openapi";
import { useCallback, useRef, useState } from "react";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

export interface ParticipantInfo {
  subject: "reading" | "math";
  gradeLevel: string;
  mode?: "practice" | "interactive";
}

interface UseAIChatOptions {
  userId: string;
  participantInfo?: ParticipantInfo;
}

interface UseAIChatReturn {
  messages: Message[];
  currentThreadId: string | null;
  isLoading: boolean;
  error: string | null;
  sendMessage: (prompt: string) => Promise<void>;
  startNewConversation: () => void;
  loadConversation: (threadId: string) => void;
}

/**
 * Custom hook for AI chat functionality using the unified chat endpoint
 * Handles SSE streaming and conversation management
 */
export const useAIChat = ({ userId, participantInfo }: UseAIChatOptions): UseAIChatReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const { mutateAsync: sendChatMessage } = useAiChatChat({
    onError: (error) => {
      console.error("Error in chat:", error);
      setError("Failed to send message. Please try again.");
    },
  });

  const sendMessage = useCallback(
    async (prompt: string) => {
      if (!prompt.trim() || isLoading) {
        return;
      }

      // Add subject and grade level context to the prompt
      const contextualPrompt = participantInfo
        ? `[${participantInfo.subject.toUpperCase()} - Grade ${participantInfo.gradeLevel}] ${
            participantInfo.subject === "math"
              ? "Please use LaTeX notation for mathematical expressions, enclosed in $ for inline math and $$ for display math. " +
                "For example: $x^2$ for inline and $$\\frac{1}{2}$$ for display. " +
                prompt
              : prompt
          }`
        : prompt;

      // Cancel any ongoing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      // Add user message to UI immediately
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        isUser: true,
        text: prompt, // Keep original prompt in UI
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      // Create initial AI message that we'll build up
      const aiMessageId = `ai-${Date.now()}`;
      const initialAiMessage: Message = {
        id: aiMessageId,
        isUser: false,
        text: "",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, initialAiMessage]);

      try {
        const response = await sendChatMessage({
          body: {
            prompt: contextualPrompt,
            threadId: currentThreadId,
            userId,
          },
        });

        // Log response details for debugging
        console.log("Response received:", {
          constructorName: response ? (Object.getPrototypeOf(response) as object).constructor.name : null,
          properties: response ? Object.keys(response as object) : [],
          response,
          type: typeof response,
        });

        if (!response) {
          throw new Error("No response received");
        }

        // Convert Blob to ReadableStream and get reader
        let reader;
        try {
          const stream = (response as Blob).stream();
          reader = stream.getReader();
          console.log("Successfully got reader from blob stream:", { reader });
        } catch (readerError) {
          console.error("Failed to get reader from blob:", {
            error: readerError,
            responseType: typeof response,
            responseValue: response,
          });
          throw readerError;
        }
        const decoder = new TextDecoder();

        let accumulatedText = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const jsonStr = line.substring(6);
              if (jsonStr.trim()) {
                try {
                  const data = JSON.parse(jsonStr);

                  // Handle thread ID from first response
                  if (data.threadId && !currentThreadId) {
                    setCurrentThreadId(data.threadId);
                  }

                  // Handle content chunks
                  if (data.content) {
                    accumulatedText += data.content;

                    // Update the streaming message with accumulated text
                    setMessages((prev) =>
                      prev.map((msg) => (msg.id === aiMessageId ? { ...msg, text: accumulatedText } : msg)),
                    );
                  }
                } catch (error) {
                  console.warn("Failed to parse SSE data:", jsonStr, error);
                }
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          console.log("Request was cancelled by user");
          return;
        }

        console.error("AI Chat error details:", {
          currentThreadId,
          error: err,
          errorType: err instanceof Error ? err.name : typeof err,
          isLoading,
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
        });

        if (err instanceof Response) {
          console.error("Response error details:", {
            body: await err.text().catch(() => "Could not read response body"),
            headers: Object.fromEntries(err.headers.entries()),
            status: err.status,
            statusText: err.statusText,
          });
        }

        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        const detailedError = `Error Type: ${err instanceof Error ? err.name : typeof err}\nMessage: ${errorMessage}`;
        console.error("Detailed error:", detailedError);

        setError(`Failed to get response from AI service: ${errorMessage}`);

        // Update the message with error content
        setMessages((prev) =>
          prev.map((msg) => (msg.id === aiMessageId ? { ...msg, text: `Error: ${detailedError}` } : msg)),
        );
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [userId, currentThreadId, isLoading, sendChatMessage, participantInfo],
  );

  const startNewConversation = useCallback(() => {
    // Cancel any ongoing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setCurrentThreadId(null);
    setMessages([]);
    setError(null);
    setIsLoading(false);
  }, []);

  const loadConversation = useCallback((threadId: string) => {
    // Cancel any ongoing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setCurrentThreadId(threadId);
    setError(null);
    setIsLoading(false);
    // Note: You might want to load conversation history here
    // by calling the useChatGetConversation hook
  }, []);

  // Note: Cleanup is handled by the components using this hook via useEffect

  return {
    currentThreadId,
    error,
    isLoading,
    loadConversation,
    messages,
    sendMessage,
    startNewConversation,
  };
};
